/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import {Action, ActionCreators} from './actions';
import { AntennaOptions } from '../../shared/entities/options/antennas';
import { ModemType, TerminalType } from '../../shared/entities/provisionedTerminal';

export interface State {
  // network options vars
  readonly networkOptions: string[] | undefined;
  readonly isRequestingNetworkOptions: boolean;
  readonly isNetworkOptionsRequestFinished: boolean;
  readonly networkOptionsRequestError?: string;

  // terminal type options vars
  readonly terminalTypeOptions: TerminalType[] | undefined;
  readonly isRequestingTerminalTypeOptions: boolean;
  readonly isTerminalTypeOptionsRequestFinished: boolean;
  readonly terminalTypeOptionsRequestError?: string;

  // platform type options vars
  readonly platformTypeOptions: string[] | undefined;
  readonly isRequestingPlatformTypeOptions: boolean;
  readonly isPlatformTypeOptionsRequestFinished: boolean;
  readonly platformTypeOptionsRequestError?: string;

  // datapath labels options vars
  readonly datapathLabelOptions: string[] | undefined;
  readonly isRequestingDatapathLabelOptions: boolean;
  readonly isDatapathLabelOptionsRequestFinished: boolean;
  readonly datapathLabelOptionsRequestError?: string;

  // customer group options vars
  readonly customerGroupOptions: string[] | undefined;
  readonly isRequestingCustomerGroupOptions: boolean;
  readonly isCustomerGroupOptionsRequestFinished: boolean;
  readonly customerGroupOptionsRequestError?: string;

  // antenna options vars
  readonly antennaOptions: AntennaOptions | undefined;
  readonly isRequestingAntennaOptions: boolean;
  readonly isAntennaOptionsRequestFinished: boolean;
  readonly antennaOptionsRequestError?: string;

  // modem type options vars
  readonly modemTypeOptions: ModemType[] | undefined;
  readonly isRequestingModemTypeOptions: boolean;
  readonly isModemTypeOptionsRequestFinished: boolean;
  readonly modemTypeOptionsRequestError?: string;
}

export const initialOptionsProvisioningState: State = {
  networkOptions: undefined,
  isRequestingNetworkOptions: false,
  isNetworkOptionsRequestFinished: false,
  networkOptionsRequestError: undefined,

  terminalTypeOptions: undefined,
  isRequestingTerminalTypeOptions: false,
  isTerminalTypeOptionsRequestFinished: false,
  terminalTypeOptionsRequestError: undefined,

  platformTypeOptions: undefined,
  isRequestingPlatformTypeOptions: false,
  isPlatformTypeOptionsRequestFinished: false,
  platformTypeOptionsRequestError: undefined,

  datapathLabelOptions: undefined,
  isRequestingDatapathLabelOptions: false,
  isDatapathLabelOptionsRequestFinished: false,
  datapathLabelOptionsRequestError: undefined,

  customerGroupOptions: undefined,
  isRequestingCustomerGroupOptions: false,
  isCustomerGroupOptionsRequestFinished: false,
  customerGroupOptionsRequestError: undefined,

  antennaOptions: undefined,
  isRequestingAntennaOptions: false,
  isAntennaOptionsRequestFinished: false,
  antennaOptionsRequestError: undefined,

  modemTypeOptions: undefined,
  isRequestingModemTypeOptions: false,
  isModemTypeOptionsRequestFinished: false,
  modemTypeOptionsRequestError: undefined,
};

export default function reducer(state: State = initialOptionsProvisioningState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    // get network options
    case ActionCreators.RequestNetworkOptions.type: {
      partialState = {
        networkOptions: undefined,
        isRequestingNetworkOptions: true,
        isNetworkOptionsRequestFinished: false,
        networkOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedNetworkOptionsSuccess.type: {
      partialState = {
        networkOptions: action.payload.response,
        isRequestingNetworkOptions: false,
        isNetworkOptionsRequestFinished: true,
        networkOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedNetworkOptionsError.type: {
      partialState = {
        isRequestingNetworkOptions: false,
        isNetworkOptionsRequestFinished: true,
        networkOptionsRequestError: action.payload.message,
      };
      break;
    }

    case ActionCreators.ClearReceivedNetworkOptionsError.type: {
      partialState = {
        networkOptionsRequestError: undefined,
      };
      break;
    }

    // get terminal type options
    case ActionCreators.RequestTerminalTypeOptions.type: {
      partialState = {
        terminalTypeOptions: undefined,
        isRequestingTerminalTypeOptions: true,
        isTerminalTypeOptionsRequestFinished: false,
        terminalTypeOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedTerminalTypeOptionsSuccess.type: {
      partialState = {
        terminalTypeOptions: action.payload.response,
        isRequestingTerminalTypeOptions: false,
        isTerminalTypeOptionsRequestFinished: true,
        terminalTypeOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedTerminalTypeOptionsError.type: {
      partialState = {
        isRequestingTerminalTypeOptions: false,
        isTerminalTypeOptionsRequestFinished: true,
        terminalTypeOptionsRequestError: action.payload.message,
      };
      break;
    }

    case ActionCreators.ClearReceivedTerminalTypeOptionsError.type: {
      partialState = {
        terminalTypeOptionsRequestError: undefined,
      };
      break;
    }

    // get platform type options
    case ActionCreators.RequestPlatformTypeOptions.type: {
      partialState = {
        platformTypeOptions: undefined,
        isRequestingPlatformTypeOptions: true,
        isPlatformTypeOptionsRequestFinished: false,
        platformTypeOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedPlatformTypeOptionsSuccess.type: {
      partialState = {
        platformTypeOptions: action.payload.response,
        isRequestingPlatformTypeOptions: false,
        isPlatformTypeOptionsRequestFinished: true,
        platformTypeOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedPlatformTypeOptionsError.type: {
      partialState = {
        isRequestingPlatformTypeOptions: false,
        isPlatformTypeOptionsRequestFinished: true,
        platformTypeOptionsRequestError: action.payload.message,
      };
      break;
    }

    case ActionCreators.ClearReceivedPlatformTypeOptionsError.type: {
      partialState = {
        platformTypeOptionsRequestError: undefined,
      };
      break;
    }

    // get data path label options
    case ActionCreators.RequestDatapathLabelOptions.type: {
      partialState = {
        datapathLabelOptions: undefined,
        isRequestingDatapathLabelOptions: true,
        isDatapathLabelOptionsRequestFinished: false,
        datapathLabelOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedDatapathLabelOptionsSuccess.type: {
      partialState = {
        datapathLabelOptions: action.payload.response,
        isRequestingDatapathLabelOptions: false,
        isDatapathLabelOptionsRequestFinished: true,
        datapathLabelOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedDatapathLabelOptionsError.type: {
      partialState = {
        isRequestingDatapathLabelOptions: false,
        isDatapathLabelOptionsRequestFinished: true,
        datapathLabelOptionsRequestError: action.payload.message,
      };
      break;
    }

    case ActionCreators.ClearReceivedDatapathLabelOptionsError.type: {
      partialState = {
        datapathLabelOptionsRequestError: undefined,
      };
      break;
    }

    // get customer group options
    case ActionCreators.RequestCustomerGroupOptions.type: {
      partialState = {
        customerGroupOptions: undefined,
        isRequestingCustomerGroupOptions: true,
        isCustomerGroupOptionsRequestFinished: false,
        customerGroupOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedCustomerGroupOptionsSuccess.type: {
      partialState = {
        customerGroupOptions: action.payload.response,
        isRequestingCustomerGroupOptions: false,
        isCustomerGroupOptionsRequestFinished: true,
        customerGroupOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedCustomerGroupOptionsError.type: {
      partialState = {
        isRequestingCustomerGroupOptions: false,
        isCustomerGroupOptionsRequestFinished: true,
        customerGroupOptionsRequestError: action.payload.message,
      };
      break;
    }

    case ActionCreators.ClearReceivedCustomerGroupOptionsError.type: {
      partialState = {
        customerGroupOptionsRequestError: undefined,
      };
      break;
    }

    // get antenna options
    case ActionCreators.RequestAntennaOptions.type: {
      partialState = {
        antennaOptions: undefined,
        isRequestingAntennaOptions: true,
        isAntennaOptionsRequestFinished: false,
        antennaOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedAntennaOptionsSuccess.type: {
      partialState = {
        antennaOptions: action.payload.response,
        isRequestingAntennaOptions: false,
        isAntennaOptionsRequestFinished: true,
        antennaOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedAntennaOptionsError.type: {
      partialState = {
        isRequestingAntennaOptions: false,
        isAntennaOptionsRequestFinished: true,
        antennaOptionsRequestError: action.payload.message,
      };
      break;
    }

    case ActionCreators.ClearReceivedAntennaOptionsError.type: {
      partialState = {
        antennaOptionsRequestError: undefined,
      };
      break;
    }

    // get modem type options
    case ActionCreators.RequestModemTypeOptions.type: {
      partialState = {
        modemTypeOptions: undefined,
        isRequestingModemTypeOptions: true,
        isModemTypeOptionsRequestFinished: false,
        modemTypeOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedModemTypeOptionsSuccess.type: {
      partialState = {
        modemTypeOptions: action.payload.response,
        isRequestingModemTypeOptions: false,
        isModemTypeOptionsRequestFinished: true,
        modemTypeOptionsRequestError: undefined,
      };
      break;
    }

    case ActionCreators.ReceivedModemTypeOptionsError.type: {
      partialState = {
        isRequestingModemTypeOptions: false,
        isModemTypeOptionsRequestFinished: true,
        modemTypeOptionsRequestError: action.payload.message,
      };
      break;
    }

    case ActionCreators.ClearReceivedModemTypeOptionsError.type: {
      partialState = {
        modemTypeOptionsRequestError: undefined,
      };
      break;
    }

    default: {
      return state;
    }
  }
  return { ...state, ...partialState };
}
